/**
 * Shared brand- and competitor-detection logic for Google AI Overview
 * responses.
 *
 * Used at three points in the pipeline:
 *   - Report storage: `executeGoogleAiOverviewQuery` in @repo/background-jobs
 *     decides whether to set `DiscoveryQueryExecution.ourBrandInSummary` and
 *     whether to create the `DiscoveryBrandMention` rows that drive
 *     per-platform counts and CompetitorSOV.
 *   - Nightly storage: `storeGoogleAiOverviewForPromptRun` does the same for
 *     standalone prompt runs.
 *   - Display: the report viewer's "✓ Brand in AI Overview" badge runs the
 *     same check live so the badge can never disagree with storage.
 *
 * Historically each call site had its own detection logic and could
 * disagree — most visibly when the brand appeared in the AI Overview's
 * prose body but NOT in the structured `brandMentions` (cited references)
 * or `textBrandMentions` (bold/headers extracted by DataForSEO). The display
 * had a regex fallback against the full prose; storage did not. The badge
 * would go green, the per-platform tab dot stayed gray, the per-query
 * mention count was off by one, and the CompetitorSOV matrix didn't show
 * the brand for that prompt. Exactly the kind of data inconsistency that
 * breaks user trust in the report.
 *
 * This module collapses those into one three-tier check:
 *   1. Cited references (`aiOverview.brandMentions`) — match by domain or name.
 *   2. Bold/header text extractions (`aiOverview.textBrandMentions`) — match by
 *      exact name or word-boundary regex.
 *   3. Full prose body (`summary` + `markdown` + `sections[].content`) —
 *      word-boundary regex against the brand name and each alias.
 *
 * Pure module: no DB or runtime deps. Safe to import from `@repo/core/client`.
 */

export interface AiOverviewReferenceLike {
  name?: string | null;
  domain?: string | null;
}

export interface AiOverviewTextMentionLike {
  name?: string | null;
}

export interface AiOverviewSectionLike {
  content?: string | null;
}

/**
 * Permissive shape we accept from callers. DataForSEO's actual response has
 * stricter typing in `@repo/database`, but we want this helper to also work
 * against the lightly-typed `data.aiOverview` the report viewer holds.
 */
export interface AiOverviewForDetection {
  summary?: string | null;
  markdown?: string | null;
  sections?: AiOverviewSectionLike[] | null;
  brandMentions?: AiOverviewReferenceLike[] | null;
  textBrandMentions?: AiOverviewTextMentionLike[] | null;
}

export interface BrandIdentityForDetection {
  /** Our brand's primary name — `property.companyName` or `brandSnapshot.data.name`. */
  brandName?: string | null;
  /**
   * Our brand's domain. Optional — pass it for storage-side detection so we
   * also match cited references by domain. Omit on the display side, which
   * historically only matches by name.
   */
  brandDomain?: string | null;
  /** Alternate names from `property.aliases`. Each checked alongside `brandName`. */
  aliases?: string[] | null;
}

export type AiOverviewDetectionTier = "reference" | "text" | "prose";

export interface DetectOurBrandInAiOverviewResult {
  found: boolean;
  /**
   * 1-based position within the structured array (`brandMentions` or
   * `textBrandMentions`) where the first match was found. `undefined` for
   * prose-fallback matches — that tier has no positional rank concept.
   */
  rank?: number;
  /** Tier at which the match was made. `undefined` when not found. */
  tier?: AiOverviewDetectionTier;
}

function normalizeDomain(value: string | null | undefined): string {
  return (value || "")
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .split("/")[0];
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function wordBoundaryRegex(term: string): RegExp {
  return new RegExp(`\\b${escapeRegExp(term)}\\b`, "i");
}

function fullAiOverviewText(ao: AiOverviewForDetection): string {
  const parts: string[] = [];
  if (ao.summary) parts.push(ao.summary);
  if (ao.markdown) parts.push(ao.markdown);
  for (const s of ao.sections ?? []) {
    if (s?.content) parts.push(s.content);
  }
  return parts.join(" ");
}

/**
 * Word-boundary check for our brand inside an arbitrary prose body.
 *
 * Shared between `detectOurBrandInAiOverview` (which scans the AI Overview
 * summary + markdown + section content) and `analyzeDiscoveryResponse` in
 * @repo/background-jobs (which scans an LLM platform's `answer_summary`).
 *
 * Centralizing this here means the AI Overview, ChatGPT, Gemini, and direct-
 * LLM (OpenAI/Claude/Perplexity) flows all answer "is the brand mentioned in
 * the body?" the same way. DataForSEO's ChatGPT / Gemini scrapers extract
 * `brands_mentioned` only from markdown bold / italic / headers via
 * `extractBrandMentionsFromMarkdown` — a brand that appears in regular prose
 * is silently dropped from that array. This helper closes the gap so storage
 * still creates a DiscoveryBrandMention row, the per-tab green dot lights
 * up, and CompetitorSOV credits the mention.
 */
export function detectOurBrandInProseText(
  text: string | null | undefined,
  identity: { brandName?: string | null; aliases?: string[] | null },
): boolean {
  if (!text) return false;
  const brandName = identity.brandName?.toLowerCase();
  if (brandName && wordBoundaryRegex(brandName).test(text)) return true;
  for (const alias of identity.aliases ?? []) {
    if (!alias || typeof alias !== "string") continue;
    if (wordBoundaryRegex(alias.toLowerCase()).test(text)) return true;
  }
  return false;
}

/**
 * Returns the names of tracked competitors that appear in an arbitrary prose
 * body. Symmetric to `detectOurBrandInProseText` — used by both the AI
 * Overview tier-3 check and the LLM analyzer's `answer_summary` fallback so
 * a competitor in prose only is still credited.
 *
 * `competitorNames` should contain canonical names + aliases, all lowercased.
 * Caller is responsible for any further canonicalization.
 */
export function detectCompetitorsInProseText(
  text: string | null | undefined,
  competitorNames: ReadonlySet<string>,
): string[] {
  if (!text) return [];
  const found = new Map<string, string>();
  for (const name of competitorNames) {
    if (!name) continue;
    if (wordBoundaryRegex(name).test(text)) {
      const key = name.toLowerCase();
      if (!found.has(key)) found.set(key, name);
    }
  }
  return Array.from(found.values());
}

/**
 * Three-tier detection of our brand in a Google AI Overview response.
 *
 * Returns the first match found, preferring structured references → text
 * mentions → prose fallback. All callers (storage + display) should route
 * through this helper so the per-platform tab dot, the "✓ Brand in AI
 * Overview" badge, the stored DiscoveryBrandMention rows, and the
 * CompetitorSOV cells can never disagree on whether the brand was
 * mentioned.
 */
export function detectOurBrandInAiOverview(
  aiOverview: AiOverviewForDetection | null | undefined,
  identity: BrandIdentityForDetection,
): DetectOurBrandInAiOverviewResult {
  if (!aiOverview) return { found: false };

  const brandName = identity.brandName?.toLowerCase();
  const brandDomain = normalizeDomain(identity.brandDomain ?? undefined);
  const aliases = (identity.aliases ?? [])
    .filter((a): a is string => !!a && typeof a === "string")
    .map((a) => a.toLowerCase());

  // Tier 1: cited references. Match by domain (most reliable) or name/alias
  // containment, matching the historical storage-side semantics.
  const references = aiOverview.brandMentions ?? [];
  for (let i = 0; i < references.length; i++) {
    const ref = references[i];
    const refDomain = normalizeDomain(ref?.domain ?? undefined);
    const refName = (ref?.name || "").toLowerCase();

    if (brandDomain && refDomain) {
      if (
        refDomain === brandDomain ||
        refDomain.includes(brandDomain) ||
        brandDomain.includes(refDomain)
      ) {
        return { found: true, rank: i + 1, tier: "reference" };
      }
    }
    if (brandName && refName.includes(brandName)) {
      return { found: true, rank: i + 1, tier: "reference" };
    }
    for (const alias of aliases) {
      if (refName.includes(alias) || alias.includes(refName)) {
        return { found: true, rank: i + 1, tier: "reference" };
      }
    }
  }

  // Tier 2: bold/header text extractions. Match by exact name or
  // word-boundary regex so we don't false-positive substrings.
  const textMentions = aiOverview.textBrandMentions ?? [];
  for (let i = 0; i < textMentions.length; i++) {
    const m = textMentions[i];
    const rawName = m?.name || "";
    const name = rawName.toLowerCase().trim();

    if (brandName && name === brandName) {
      return { found: true, rank: i + 1, tier: "text" };
    }
    if (brandName && wordBoundaryRegex(brandName).test(rawName)) {
      return { found: true, rank: i + 1, tier: "text" };
    }
    for (const alias of aliases) {
      if (name === alias) {
        return { found: true, rank: i + 1, tier: "text" };
      }
      if (wordBoundaryRegex(alias).test(rawName)) {
        return { found: true, rank: i + 1, tier: "text" };
      }
    }
  }

  // Tier 3: prose body fallback. Routes through the shared
  // `detectOurBrandInProseText` helper so the same regex semantics back
  // every "is the brand in body text?" check — AI Overview, ChatGPT, Gemini.
  const prose = fullAiOverviewText(aiOverview);
  if (detectOurBrandInProseText(prose, { brandName, aliases })) {
    return { found: true, tier: "prose" };
  }

  return { found: false };
}

/**
 * Returns the names of tracked competitors that appear in the AI Overview
 * (across cited references, bold/headers, AND the prose body). Symmetric to
 * `detectOurBrandInAiOverview` but returns every match (not just the first)
 * so storage can credit each competitor independently.
 *
 * Caller supplies `competitorNames` as the set of lowercased canonical
 * names *plus* their aliases — exact-match lookup keeps tiers 1 and 2 fast.
 * The prose fallback iterates the set, which keeps the cost bounded by the
 * tracked-competitor count.
 *
 * Returned strings are deduplicated case-insensitively. For reference and
 * text-tier matches we return the AI Overview's own casing; for prose
 * matches we fall through to the lowercased Set entry — the caller can
 * canonicalize from its own competitor catalog if it cares about display.
 */
export function detectCompetitorsInAiOverview(
  aiOverview: AiOverviewForDetection | null | undefined,
  identity: {
    /** Lowercased canonical names plus aliases. */
    competitorNames: ReadonlySet<string>;
    /** Lowercased, normalized competitor domains. */
    competitorDomains: ReadonlySet<string>;
  },
): string[] {
  if (!aiOverview) return [];

  const { competitorNames, competitorDomains } = identity;
  const found = new Map<string, string>(); // dedupe key → display value

  const record = (display: string) => {
    const dedupe = display.toLowerCase().trim();
    if (!dedupe) return;
    if (!found.has(dedupe)) found.set(dedupe, display);
  };

  // Tier 1: references — match by name or domain.
  for (const ref of aiOverview.brandMentions ?? []) {
    const rawName = ref?.name || "";
    const refName = rawName.toLowerCase();
    const refDomain = normalizeDomain(ref?.domain ?? undefined);
    if (competitorNames.has(refName)) record(rawName);
    else if (refDomain && competitorDomains.has(refDomain)) record(rawName);
  }

  // Tier 2: bold/header extractions — exact name match.
  for (const m of aiOverview.textBrandMentions ?? []) {
    const rawName = m?.name || "";
    const name = rawName.toLowerCase().trim();
    if (competitorNames.has(name)) record(rawName);
  }

  // Tier 3: prose body fallback. Routes through `detectCompetitorsInProseText`
  // so the same regex semantics back every competitor body-text check across
  // AI Overview, ChatGPT, Gemini.
  const prose = fullAiOverviewText(aiOverview);
  for (const name of detectCompetitorsInProseText(prose, competitorNames)) {
    record(name);
  }

  return Array.from(found.values());
}
